import { Check, Expand } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { cn } from "@/modern/utils";
import type { MouseEvent } from "react";
import type { PromoteSurfaceAsset } from "../api/types";

type Props = {
  asset: PromoteSurfaceAsset;
  selected: boolean;
  focused: boolean;
  readOnly?: boolean;
  onSelect: (event: MouseEvent) => void;
  onOpen: () => void;
};

export default function PromoteSurfaceCard({
  asset,
  selected,
  focused,
  readOnly,
  onSelect,
  onOpen,
}: Props) {
  const src = asset.thumbnail_url || asset.url || "";

  return (
    <div
      className={cn(
        "group relative aspect-square rounded-md bg-muted outline-none transition",
        !readOnly && selected
          ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
          : "ring-1 ring-border/60 hover:ring-border",
        focused && !selected && "ring-1 ring-foreground/25"
      )}
    >
      {/* Clip media only — keep selection ring outside overflow so it isn’t cut off */}
      <div className="absolute inset-0 overflow-hidden rounded-md">
        <button
          type="button"
          aria-pressed={readOnly ? undefined : selected}
          aria-label={
            readOnly
              ? asset.caption || `Asset ${asset.id}`
              : selected
                ? `Deselect ${asset.caption || asset.id}`
                : `Select ${asset.caption || asset.id}`
          }
          onClick={readOnly ? onOpen : onSelect}
          onDoubleClick={(event) => {
            event.preventDefault();
            onOpen();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onOpen();
            }
          }}
          className="absolute inset-0 block h-full w-full text-left"
        >
          {src ? (
            <img
              src={src}
              alt={asset.alt_text || asset.caption || `Asset ${asset.id}`}
              loading="lazy"
              className={cn(
                "h-full w-full object-cover transition",
                selected && !readOnly && "brightness-90"
              )}
              draggable={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
              No preview
            </div>
          )}
        </button>

        {selected && !readOnly && (
          <div className="pointer-events-none absolute inset-0 bg-primary/15" />
        )}

        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-black/55 to-transparent",
            "opacity-0 transition group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          )}
          aria-hidden
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          className={cn(
            "absolute right-2 top-2 z-20 h-9 cursor-pointer gap-1.5 rounded-lg border-2 border-white",
            "bg-white px-3.5 text-xs font-bold text-neutral-900 shadow-[0_4px_14px_rgba(0,0,0,0.45)]",
            "hover:bg-neutral-100 hover:text-neutral-950 hover:shadow-[0_6px_18px_rgba(0,0,0,0.55)] hover:-translate-y-0.5",
            "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40",
            "active:translate-y-0 active:scale-[0.96] active:shadow-md",
            "opacity-0 transition duration-150 group-hover:opacity-100 focus-visible:opacity-100",
            "[@media(hover:none)]:opacity-100"
          )}
        >
          <Expand className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
          See details
        </Button>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-8">
          <div className="truncate text-[11px] text-white/95">{asset.caption || "Untitled"}</div>
          <div className="mt-0.5 flex items-center justify-between gap-1">
            <span className="rounded bg-black/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/90">
              {asset.verification_status?.replaceAll("_", " ")}
            </span>
            {asset.actioned_by_name && (
              <span className="truncate text-[10px] text-white/80">{asset.actioned_by_name}</span>
            )}
          </div>
        </div>

        {!readOnly && (
          <span
            className={cn(
              "pointer-events-none absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md border shadow-sm transition",
              selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-white/70 bg-black/35 text-transparent group-hover:border-white group-hover:bg-black/50"
            )}
            aria-hidden
          >
            <Check className={cn("h-3.5 w-3.5", selected ? "opacity-100" : "opacity-0 group-hover:opacity-40")} />
          </span>
        )}
      </div>
    </div>
  );
}
